import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Button,
  Input,
  Table,
  Loader,
  Dimmer,
  Message,
  Icon,
} from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { getWithAuth } from '../../api';
import messages from '../../messages';

function expandPartRow(part) {
  if (!Array.isArray(part)) {
    return part;
  }

  const [moto_no, description, note] = part;
  return { moto_no, description, note };
}

function PartFinderModal({ onClose }) {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(true);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const scrollRef = useRef(null);

  const PAGE_SIZE = 50;

  const copyToClipboard = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    getWithAuth('parts')
      .then((data) => {
        setParts(data.map(expandPartRow));
        setLoading(false);
      })
      .catch((err) => {
        setError('Failed to load parts.');
        setLoading(false);
      });
  }, []);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const query = search.trim().toLowerCase();
  const filtered =
    query.length >= 3
      ? parts.filter(
          (p) =>
            p.moto_no.toLowerCase().includes(query) ||
            p.description.toLowerCase().includes(query)
        )
      : parts;
  const visible = filtered.slice(0, visibleCount);

  // Reset visible count whenever the filtered list changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [query]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filtered.length));
      }
    },
    [filtered.length]
  );

  return (
    <Modal open={open} onClose={handleClose} size="large">
      <Modal.Header>
        {formatMessage(messages.partSearch)}
        <Button
          circular
          icon="close"
          title={formatMessage(messages.close)}
          floated="right"
          onClick={handleClose}
        />
      </Modal.Header>
      <Modal.Content>
        <Message warning>
          Not all parts are available - check with your local representative
        </Message>
        <Input
          fluid
          icon="search"
          placeholder="Search by part number or description (min. 3 characters)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: '1rem' }}
        />
        {loading && (
          <Dimmer active inverted>
            <Loader>Loading parts...</Loader>
          </Dimmer>
        )}
        {error && <Message negative>{error}</Message>}
        {!loading && !error && (
          <div
            ref={scrollRef}
            style={{ height: '55vh', overflowY: 'auto' }}
            onScroll={handleScroll}
          >
            <Table
              celled
              compact
              striped
              style={{ tableLayout: 'fixed', width: '100%' }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ width: '20%' }}>
                    Part Number
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '50%' }}>
                    Description
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '27.5%' }}>
                    Note
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '2.5%' }} />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={4} textAlign="center">
                      No parts found.
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  visible.map((part) => (
                    <Table.Row key={part.moto_no}>
                      {[part.moto_no, part.description, part.note].map(
                        (val, i) => (
                          <Table.Cell
                            key={i}
                            style={{
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            }}
                            onClick={() =>
                              copyToClipboard(val ?? '', `${part.moto_no}-${i}`)
                            }
                          >
                            {val}
                          </Table.Cell>
                        )
                      )}
                      <Table.Cell
                        textAlign="center"
                        style={{ padding: '0.3rem' }}
                      >
                        <Icon
                          name="copy outline"
                          link
                          title="Copy row"
                          onClick={() =>
                            copyToClipboard(
                              [part.moto_no, part.description, part.note]
                                .filter(Boolean)
                                .join('\t'),
                              `row-${part.moto_no}`
                            )
                          }
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
            {visibleCount < filtered.length && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '0.5rem',
                  color: '#888',
                }}
              >
                Showing {visibleCount} of {filtered.length} — scroll down to
                load more
              </div>
            )}
          </div>
        )}
      </Modal.Content>
      {copiedKey && (
        <div
          style={{
            position: 'fixed',
            bottom: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            padding: '0.4rem 1rem',
            borderRadius: '4px',
            fontSize: '0.9rem',
            pointerEvents: 'none',
            zIndex: 10000,
          }}
        >
          Copied!
        </div>
      )}
      <Modal.Actions>
        <Button onClick={handleClose}>{formatMessage(messages.close)}</Button>
      </Modal.Actions>
    </Modal>
  );
}

export default PartFinderModal;
